import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  computed,
  inject,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { PaHttpClient } from 'src/app/clients/http-client';
import { AuthService } from 'src/app/shared/services/auth.service';
import { ToastrService } from 'src/app/shared/services/toastr.service';
import { GmapService } from 'src/app/shared/services/gmap.service';
import { AssignSpPopupComponent, AssignType } from 'src/app/views/requests/detail/assign-sp-popup/assign-sp-popup.component';
import { ContactListPopupComponent } from 'src/app/shared/components/contact-list-popup/contact-list-popup.component';
import { AddContactPopupComponent } from 'src/app/views/contacts/add-contact-popup/add-contact-popup.component';
import { ActivityRefreshService } from 'src/app/shared/services/activity-refresh.service';

declare const google: any;

type GuestEntryStep = 'select_type' | 'select_contacts' | 'pickup_datetime' | 'review_details';

interface EntryType {
  value: string;
  label: string;
  description: string;
  icon: string;
}

const ENTRY_TYPES: EntryType[] = [
  {
    value: 'one_time_entry',
    label: 'One Time Entry',
    description: 'Grants the guest single-use access, ideal for short-term visits',
    icon: 'assets/icons/onetimeentry.svg',
  },
  {
    value: 'permanent_pass',
    label: 'Permanent Pass',
    description: 'Provides unrestricted, ongoing access for frequent or long-term visitors',
    icon: 'assets/icons/permanentpass.svg',
  },
  {
    value: 'temporary_pass',
    label: 'Temporary Pass',
    description: 'Allows multiple entries until expiration, suitable for recurring short-term access',
    icon: 'assets/icons/temporarypass.svg',
  },
  {
    value: 'restricted_entry',
    label: 'Restricted Entry',
    description: 'Request the front desk to ensure unauthorized individuals are limited or denied entry',
    icon: 'assets/icons/restrictedentry.svg',
  },
];

function getDefaultPickupDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

@Component({
  selector: 'pa-guests-entry-create',
  imports: [CommonModule, DialogModule, AssignSpPopupComponent, ContactListPopupComponent, AddContactPopupComponent],
  templateUrl: './guests-entry-create.component.html',
  styleUrls: ['./guests-entry-create.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PaHttpClient],
  host: {
    '(document:click)': 'onDocClick($event)',
    '(window:resize)': 'onWindowResize()',
    '(window:orientationchange)': 'onWindowResize()',
  },
})
export class GuestsEntryCreateComponent implements OnInit {
  readonly close = output<void>();
  readonly requestComplete = output<unknown>();

  private readonly httpClient = inject(PaHttpClient);
  private readonly authService = inject(AuthService);
  private readonly toastr = inject(ToastrService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly gmapService = inject(GmapService);
  private readonly activityRefresh = inject(ActivityRefreshService);

  readonly currentUser = this.authService.getUser();
  readonly entryTypes = ENTRY_TYPES;

  // ── Navigation ──────────────────────────────────────────────
  readonly currentStep = signal<GuestEntryStep>('select_type');
  readonly isMobileView = signal(false);

  // ── Step 1: Entry Type ───────────────────────────────────────
  readonly selectedEntryType = signal<EntryType | null>(null);

  // ── Step 2: Contacts ─────────────────────────────────────────
  readonly selectedGuests = signal<any[]>([]);
  readonly selectedManagement = signal<any | null>(null);
  readonly selectedFrontDesk = signal<any | null>(null);
  readonly returnToReview = signal(false);
  readonly isAddContactPopupOpen = signal(false);

  // ── Step 3: Date & Time ──────────────────────────────────────
  readonly selectedDate = signal<string>(getDefaultPickupDate());
  readonly selectedHour = signal<string>('09');
  readonly selectedMinute = signal<string>('00');
  readonly showHourDropdown = signal(false);
  readonly showMinuteDropdown = signal(false);

  readonly hoursList: { value: string; title: string }[] = Array.from({ length: 24 }, (_, x) => {
    const v = x < 10 ? '0' + x : String(x);
    return { value: v, title: `${v} (${x % 12 || 12} ${x < 12 ? 'AM' : 'PM'})` };
  });
  readonly minutesList: string[] = Array.from({ length: 60 }, (_, x) => String(x).padStart(2, '0'));

  // ── Location ─────────────────────────────────────────────────
  readonly showLocationDialog = signal(false);
  readonly locationAddress = signal('');
  readonly addressInput = signal('');
  readonly autocompleteResults = signal<any[]>([]);
  readonly locationSearchCenter = signal<{ lat: number; lng: number } | null>(null);

  @ViewChild('locationMapEl') private locationMapEl?: ElementRef<HTMLDivElement>;
  private locationMapInstance: any = null;
  private locationMapInitialized = false;
  private locationAutocompleteService: any = null;
  private locationPickupOverlay: any = null;

  readonly savedAddresses = computed<{ label: string }[]>(() => {
    const addrs: any[] = (this.currentUser as any)?.addresses ?? [];
    return addrs.map(a => ({
      label: [a.address, a.city, a.state?.short_name ?? a.state, a.zipcode].filter(Boolean).join(', '),
    }));
  });

  // ── Assign popup ─────────────────────────────────────────────
  readonly showAssignPopup = signal(false);
  readonly assignPopupType = signal<AssignType>('management');
  readonly assignPopupTitle = signal('');

  // ── Submit ───────────────────────────────────────────────────
  readonly isSubmitting = signal(false);
  readonly showSuccessPopup = signal(false);

  ngOnInit(): void {
    this.isMobileView.set(window.innerWidth < 768);
    const addrs: any[] = (this.currentUser as any)?.addresses ?? [];
    if (addrs.length) {
      const a = addrs[0];
      const label = [a.address, a.city, a.state?.short_name ?? '', a.zipcode].filter(Boolean).join(', ');
      this.locationAddress.set(label);
      const lat = Number(a.lat ?? a.latitude);
      const lng = Number(a.lng ?? a.longitude);
      if (lat && lng) {
        this.locationSearchCenter.set({ lat, lng });
      } else if (label) {
        this.gmapService.api.then(() => {
          const geocoder = new google.maps.Geocoder();
          geocoder.geocode({ address: label }, (results: any, status: any) => {
            if (status === 'OK' && results?.[0]?.geometry?.location) {
              this.locationSearchCenter.set({
                lat: results[0].geometry.location.lat(),
                lng: results[0].geometry.location.lng(),
              });
            }
          });
        }).catch(() => {});
      }
    }
  }

  onWindowResize(): void {
    this.isMobileView.set(window.innerWidth < 768);
  }

  onDocClick(e: MouseEvent): void {
    if (this.showHourDropdown() || this.showMinuteDropdown()) {
      if (!(e.target as HTMLElement).closest('.time-dropdown-wrap')) {
        this.showHourDropdown.set(false);
        this.showMinuteDropdown.set(false);
      }
    }
  }

  // ── Navigation ───────────────────────────────────────────────
  private setStep(step: GuestEntryStep): void {
    this.currentStep.set(step);
    this.cdr.markForCheck();
  }

  goToDateTimeFromReview(): void {
    this.returnToReview.set(true);
    this.setStep('pickup_datetime');
  }

  onBack(): void {
    const s = this.currentStep();
    if (s === 'select_type') { this.close.emit(); }
    else if (s === 'select_contacts') {
      if (this.returnToReview()) { this.returnToReview.set(false); this.setStep('review_details'); }
      else this.setStep('select_type');
    }
    else if (s === 'pickup_datetime') {
      if (this.returnToReview()) { this.returnToReview.set(false); this.setStep('review_details'); }
      else this.setStep('select_contacts');
    }
    else if (s === 'review_details') { this.setStep('pickup_datetime'); }
  }

  onClose(): void {
    this.close.emit();
  }

  // ── Step 1 ───────────────────────────────────────────────────
  selectEntryType(type: EntryType): void {
    this.selectedEntryType.set(type);
    this.setStep('select_contacts');
  }

  // ── Step 2 ───────────────────────────────────────────────────
  openGuestSelector(): void {
    this.returnToReview.set(true);
    this.setStep('select_contacts');
  }

  onGuestListPopupClose(event: any): void {
    if (event.back) { this.onBack(); return; }
    if (event.close) {
      const contacts: any[] = event.data?.addedcontacts ?? [];
      this.selectedGuests.set(contacts);
      if (this.returnToReview()) {
        this.returnToReview.set(false);
        this.setStep('review_details');
      } else {
        this.setStep('pickup_datetime');
      }
    }
  }

  onAddGuestClicked(): void {
    this.isAddContactPopupOpen.set(true);
  }

  onAddContactPopupClose(): void {
    this.isAddContactPopupOpen.set(false);
  }

  removeGuest(index: number): void {
    this.selectedGuests.update(list => list.filter((_, i) => i !== index));
  }

  // ── Step 3 ───────────────────────────────────────────────────
  onDateTimeNext(): void {
    this.returnToReview.set(false);
    this.setStep('review_details');
  }

  getFormattedDate(): string {
    const [year, month, day] = this.selectedDate().split('-').map(Number);
    const d = new Date(year, month - 1, day);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[d.getMonth()]} ${String(d.getDate()).padStart(2, '0')}`;
  }

  getDayOfWeek(): string {
    const [year, month, day] = this.selectedDate().split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-US', { weekday: 'long' });
  }

  getHourLabel(): string {
    const h = parseInt(this.selectedHour(), 10);
    return `${h % 12 || 12} ${h < 12 ? 'AM' : 'PM'}`;
  }

  openDatePicker(input: HTMLInputElement): void {
    try { input.showPicker(); } catch { input.click(); }
  }

  onDateChange(e: Event): void {
    this.selectedDate.set((e.target as HTMLInputElement).value);
  }

  toggleHourDropdown(e: MouseEvent): void {
    e.stopPropagation();
    this.showHourDropdown.set(!this.showHourDropdown());
    this.showMinuteDropdown.set(false);
  }

  toggleMinuteDropdown(e: MouseEvent): void {
    e.stopPropagation();
    this.showMinuteDropdown.set(!this.showMinuteDropdown());
    this.showHourDropdown.set(false);
  }

  selectHour(i: number): void {
    this.selectedHour.set(this.hoursList[i].value);
    this.showHourDropdown.set(false);
  }

  selectMinute(i: number): void {
    this.selectedMinute.set(this.minutesList[i]);
    this.showMinuteDropdown.set(false);
  }

  getPickupDateTimeLabel(): string {
    const [year, month, day] = this.selectedDate().split('-').map(Number);
    const d = new Date(year, month - 1, day);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const h = parseInt(this.selectedHour(), 10);
    return `${months[d.getMonth()]} ${d.getDate()}, ${days[d.getDay()]} ${h % 12 || 12}:${this.selectedMinute()}${h < 12 ? 'AM' : 'PM'}`;
  }

  // ── Assign popup ─────────────────────────────────────────────
  openAssignPopup(type: AssignType, title: string): void {
    this.assignPopupType.set(type);
    this.assignPopupTitle.set(title);
    this.showAssignPopup.set(true);
  }

  onAssignSelected(item: any): void {
    this.showAssignPopup.set(false);
    if (!item) return;
    if (this.assignPopupType() === 'management') {
      this.selectedManagement.set(item);
    } else if (this.assignPopupType() === 'front_desk') {
      this.selectedFrontDesk.set(item);
    }
  }

  // ── Submit ───────────────────────────────────────────────────
  onCreateRequest(): void {
    if (this.isSubmitting()) return;
    if (!this.selectedEntryType()) {
      this.toastr.showError('Please select an entry type.');
      return;
    }

    this.isSubmitting.set(true);

    const center = this.locationSearchCenter();
    const latLong = center ? `${center.lat},${center.lng}` : '';
    const location = this.locationAddress() || this.getUserAddress();

    const payload: Record<string, unknown> = {
      service_type: 'guest_entry',
      request_type: this.selectedEntryType()!.value,
      pickup_date: this.selectedDate(),
      pickup_time: `${this.selectedHour()}:${this.selectedMinute()}:00`,
      pickup_location: location,
      pickup_location_lat_long: latLong,
      apartment: this.getUserApartment(),
      ...(this.selectedGuests().length && { guest_members: this.selectedGuests().map(g => String(g.user_id)) }),
      ...(this.selectedManagement() && { management_id: String(this.selectedManagement()!.user_id) }),
      ...(this.selectedFrontDesk() && { frontdesk_id: String(this.selectedFrontDesk()!.user_id) }),
    };

    this.httpClient.post('request/add', payload).subscribe({
      next: () => {
        this.isSubmitting.set(false);
        this.activityRefresh.triggerRefresh();
        this.showSuccessPopup.set(true);
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        this.isSubmitting.set(false);
        this.toastr.showError(err?.error?.message || 'Failed to create request. Please try again.');
        this.cdr.markForCheck();
      },
    });
  }

  onSuccessOk(): void {
    this.showSuccessPopup.set(false);
    this.requestComplete.emit({ success: true });
    this.close.emit();
  }

  // ── Helpers ──────────────────────────────────────────────────
  getUserFullName(): string {
    const u = this.currentUser;
    return `${u?.first_name ?? ''} ${u?.last_name ?? ''}`.trim();
  }

  getUserAvatar(): string {
    return this.currentUser?.profile_picture || 'assets/images/dummy-avatar.jpg';
  }

  isPictureBlocked(status: string | null | undefined): boolean {
    return status === 'flagged' || status === 'blocked';
  }

  getContactDisplayName(c: any): string {
    return c?.management_name || c?.service_provider_name || `${c?.first_name ?? ''} ${c?.last_name ?? ''}`.trim() || 'Unknown';
  }

  getContactInitials(c: any): string {
    const name = this.getContactDisplayName(c);
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '?';
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  getAssigneeAvatar(item: any): string | null {
    const url = item?.profile_picture || item?.documents?.[0]?.url;
    return typeof url === 'string' && url.trim() ? url : null;
  }

  getUserAddress(): string {
    const addrs: any[] = this.currentUser?.addresses ?? [];
    if (!addrs?.length) return '';
    const a = addrs[0];
    return [a.address, a.city, a.state?.short_name ?? '', a.zipcode].filter(Boolean).join(', ');
  }

  private getUserApartment(): string {
    const addrs: any[] = (this.currentUser as any)?.addresses ?? [];
    if (!addrs.length) return '';
    const a = addrs[0];
    return a.apartment ?? a.apt ?? a.unit ?? '';
  }

  // ── Location dialog ──────────────────────────────────────────
  openLocationDialog(): void {
    this.addressInput.set(this.locationAddress());
    this.autocompleteResults.set([]);
    this.showLocationDialog.set(true);
    this.cdr.markForCheck();
    setTimeout(() => this.initLocationMap(), 300);
  }

  dismissLocationDialog(): void {
    this.teardownLocationMap();
    this.showLocationDialog.set(false);
  }

  onLocationDialogNext(): void {
    const addr = this.addressInput().trim();
    if (addr) this.locationAddress.set(addr);
    this.teardownLocationMap();
    this.showLocationDialog.set(false);
  }

  onAddressInput(value: string): void {
    this.addressInput.set(value);
    // Clear resolved coordinates so stale lat_long is not submitted if the user edits without re-searching
    this.locationSearchCenter.set(null);
    if (!value.trim() || !this.locationAutocompleteService) {
      this.autocompleteResults.set([]);
      return;
    }
    this.locationAutocompleteService.getPlacePredictions(
      { input: value, types: ['address'] },
      (predictions: any[] | null) => {
        this.autocompleteResults.set(predictions ?? []);
        this.cdr.markForCheck();
      },
    );
  }

  onSelectAutocomplete(prediction: any): void {
    this.addressInput.set(prediction.description);
    this.autocompleteResults.set([]);
    this.gmapService.api.then(() => {
      const ps = new google.maps.places.PlacesService(this.locationMapInstance);
      ps.getDetails({ placeId: prediction.place_id, fields: ['geometry'] }, (place: any, status: string) => {
        if (status === 'OK' && place?.geometry?.location) {
          const loc = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
          this.locationSearchCenter.set(loc);
          this.buildPickupMarker(loc, prediction.description);
        }
        this.cdr.markForCheck();
      });
    });
  }

  onAddressSearch(): void {
    this.autocompleteResults.set([]);
    const searchText = this.addressInput().trim();
    if (!searchText) return;
    this.gmapService.api.then(() => {
      try {
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ address: searchText }, (results: any, status: any) => {
          if (status === 'OK' && results?.[0]?.geometry?.location) {
            const loc = { lat: results[0].geometry.location.lat(), lng: results[0].geometry.location.lng() };
            this.locationSearchCenter.set(loc);
            this.buildPickupMarker(loc, searchText);
          }
          this.cdr.markForCheck();
        });
      } catch {
        this.toastr.showError('Map is still loading. Please try again.');
      }
    });
  }

  selectSavedAddress(label: string): void {
    this.addressInput.set(label);
    this.autocompleteResults.set([]);
    this.onAddressSearch();
  }

  private async initLocationMap(): Promise<void> {
    if (this.locationMapInitialized || !this.locationMapEl?.nativeElement) return;
    try {
      await this.gmapService.api;
      const center = this.locationSearchCenter() ?? { lat: 40.7128, lng: -74.006 };
      this.locationMapInstance = new google.maps.Map(this.locationMapEl.nativeElement, {
        center, zoom: 13, mapTypeControl: false, fullscreenControl: false, streetViewControl: false,
        styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }],
      });
      this.locationAutocompleteService = new google.maps.places.AutocompleteService();
      this.locationMapInitialized = true;
      const sc = this.locationSearchCenter();
      const addr = this.addressInput().trim();
      if (sc && addr) this.buildPickupMarker(sc, addr);
      this.cdr.markForCheck();
    } catch {}
  }

  private buildPickupMarker(loc: { lat: number; lng: number }, address: string): void {
    if (this.locationPickupOverlay) {
      try { this.locationPickupOverlay.setMap(null); } catch {}
      this.locationPickupOverlay = null;
    }
    if (!this.locationMapInstance) return;
    const latlng = new google.maps.LatLng(loc.lat, loc.lng);
    const safe = address.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const el = document.createElement('div');
    el.style.cssText = 'position:absolute;transform:translate(-50%,-100%);display:flex;flex-direction:column;align-items:center;pointer-events:none;z-index:1000';
    el.innerHTML = `<div style="background:#fff;border-radius:5px;padding:12px 14px;box-shadow:0 4px 20px rgba(0,0,0,.22);min-width:174px;max-width:220px;"><div style="font-size:12px;font-weight:600;color:#17B09A;margin-bottom:5px;">Pickup Location</div><div style="font-size:14px;font-weight:600;color:#192A3E;">${safe}</div></div>`;
    const PickupOverlay = class extends google.maps.OverlayView {
      private _el: HTMLDivElement; private _ll: any;
      constructor(e: HTMLDivElement, l: any) { super(); this._el = e; this._ll = l; }
      onAdd(): void { (this as any)['getPanes']().floatPane.appendChild(this._el); }
      draw(): void { const pt = (this as any)['getProjection']().fromLatLngToDivPixel(this._ll); if (pt) { this._el.style.left = `${pt.x}px`; this._el.style.top = `${pt.y}px`; } }
      onRemove(): void { this._el.parentNode?.removeChild(this._el); }
    };
    this.locationPickupOverlay = new PickupOverlay(el, latlng);
    this.locationPickupOverlay.setMap(this.locationMapInstance);
    this.locationMapInstance.panTo(latlng);
    this.locationMapInstance.setZoom(15);
  }

  private teardownLocationMap(): void {
    if (this.locationPickupOverlay) { try { this.locationPickupOverlay.setMap(null); } catch {} this.locationPickupOverlay = null; }
    this.locationMapInstance = null;
    this.locationMapInitialized = false;
    this.locationAutocompleteService = null;
  }
}
